import {i18n} from '@online-offer/i18n';
import {
  Box,
  Center,
  getValueByDevice,
  Heading,
  Hide,
  HStack,
  Image,
  Show,
  Space,
  Text,
  useDevice,
  VStack,
} from 'platform/foundation';
import {useLocale} from 'platform/locale';

import {useParams} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';
import {noop} from 'ramda-adjunct';

import {useLazyGetBuyingOnlineOfferPdfQuery} from '../../api/buyingOnlineOfferApi';
import {useLazyGetPdfByOfferIdQuery} from '../../api/onlineOfferApi';
import {APPLICATION_NAME} from '../../consts/applicationName';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {downloadUri} from '../../utils/downloadUri';
import {getWorkspaceCustomization} from '../../utils/getWorkspaceCustomization';
import {getWorkspaceFromUri} from '../../utils/getWorkspaceFromUri';
import {makeStorageResizeUrl} from '../../utils/makeStorageResizeUrl';
import {normalizeString} from '../../utils/normalizeString';
import {Button} from '../Button/Button';
import {ButtonIcon} from '../ButtonIcon/ButtonIcon';
import {FetchingButton} from '../FetchingButton/FetchingButton';
import {OfferDirectionSwitcher} from '../OfferDirectionSwitcher/OfferDirectionSwitcher';

// we're multiplying by 2 for larger screens to have better quality
const LOGO_WIDTH = 220 * 2;
const LOGO_HEIGHT = 44 * 2;
const DEFAULT_PDF_FILENAME = 'online-offer';

const {workspace} = getWorkspaceFromUri();

function OfferForCustomer() {
  const {activeReservation, customer} = useGetOnlineOfferData();
  const customerName = activeReservation?.customer?.name ?? customer;

  return (
    <VStack>
      <Text size="small" color="secondary">
        {i18n.t('navigation.offerFor', {code: activeReservation?.businessCase?.code ?? ''})}
      </Text>
      <Heading size={4}>{customerName}</Heading>
    </VStack>
  );
}

export function Navigation() {
  const {logo: applicationLogo} = getWorkspaceCustomization();
  const {vehicle, tenantLogoUrl, direction} = useGetOnlineOfferData();
  const params = useParams<{offerId: string}>();
  const locale = useLocale();
  const device = useDevice();
  const [getSellingPdf, sellingPdfState] = useLazyGetPdfByOfferIdQuery();
  const [getBuyingPdf, buyingPdfState] = useLazyGetBuyingOnlineOfferPdfQuery();

  const isBuying = direction === 'BUYING';
  const {isFetching, isSuccess, isError} = isBuying ? buyingPdfState : sellingPdfState;

  const resizedLogoUrl = tenantLogoUrl
    ? makeStorageResizeUrl(tenantLogoUrl, {
        width: LOGO_WIDTH,
        height: LOGO_HEIGHT,
        withoutEnlargement: true,
      })
    : undefined;

  const downloadPdf = () => {
    // The PDF is built from the deal vehicle.
    if (!params.offerId || !workspace || isNil(vehicle)) {
      return;
    }

    const filename = vehicle?.title ? normalizeString(vehicle.title) : DEFAULT_PDF_FILENAME;
    const pdfRequest = isBuying
      ? getBuyingPdf({offerId: params.offerId, workspace, locale: locale.language, filename})
      : getSellingPdf({
          offerId: params.offerId,
          workspace,
          locale: locale.language,
          filename,
        });

    pdfRequest
      .unwrap()
      .then((response) => downloadUri(response.uri))
      .catch(noop);
  };

  const onShare = () =>
    navigator.share({
      title: `${vehicle?.title} • ${APPLICATION_NAME}`,
      url: window.location.href,
    });

  return (
    <>
      <Box
        position={getValueByDevice(device, 'sticky', 'static', 'static', 'static')}
        top={0}
        backgroundColor="general.white"
        zIndex="MENU_BAR"
        data-testid="navigationContainer"
      >
        <Center justify="center" width="100%">
          <Box
            flexGrow={1}
            maxWidth={432}
            paddingTop={[2, 4, 4, 6]}
            paddingBottom={[0, 4, 4, 6]}
            paddingHorizontal={[3, 8, 8, 20]}
          >
            <HStack align="center" justify="space-between">
              <Box>
                {resizedLogoUrl ? <Image maxHeight={11} src={resizedLogoUrl} /> : applicationLogo}
              </Box>
              <Hide onMobile>
                <Box paddingLeft={8}>
                  <HStack align="center" spacing={4}>
                    <OfferForCustomer />
                    <OfferDirectionSwitcher />
                  </HStack>
                </Box>
              </Hide>
              <Space horizontal={4} fillAvailable />
              <HStack spacing={2}>
                <Show when={isNotNil(vehicle)}>
                  <Show onMobile>
                    <ButtonIcon
                      data-testid="downloadButton"
                      variant="outline"
                      icon="download"
                      onClick={downloadPdf}
                      size="small"
                    />
                  </Show>
                  <Hide onMobile>
                    <FetchingButton
                      data-testid="downloadButton"
                      variant="outline"
                      title={i18n.t('downloadPdf')}
                      icon="download"
                      isSuccess={isSuccess}
                      isFetching={isFetching}
                      isError={isError}
                      onClick={downloadPdf}
                    />
                  </Hide>
                </Show>
                <Show when={navigator.share}>
                  <Show onMobile>
                    <ButtonIcon
                      data-testid="shareButton"
                      variant="outline"
                      icon="share"
                      onClick={onShare}
                      size="small"
                    />
                  </Show>
                  <Hide onMobile>
                    <Button
                      data-testid="shareButton"
                      variant="outline"
                      title={i18n.t('share')}
                      leftIcon="share"
                      onClick={onShare}
                    />
                  </Hide>
                </Show>
              </HStack>
            </HStack>
            <Show onMobile>
              <Box paddingTop={3}>
                <VStack spacing={3} align="flex-start">
                  <OfferForCustomer />
                  <OfferDirectionSwitcher />
                </VStack>
              </Box>
            </Show>
          </Box>
        </Center>
      </Box>
    </>
  );
}
